// Écran central : message de situation + bouton d'action (Jouer / Niveau suivant / Recommencer)
import { $, show, hide, escapeHTML } from './dom.js';
import { pickComment } from '../data/comments.js';
import { OVERLAY_BUTTON_DELAY_MS } from '../config/constants.js';
import { formatChargeTime } from '../core/battery.js';

// Couleurs sur la carte sombre de l'overlay (charte Halloween)
const COLOR_BY_SITUATION = {
  start: 'text-slate-200',
  win: 'text-yellow-300',
  survive: 'text-emerald-300',
  lose: 'text-rose-400',
};

// Libellé des boutons : un simple verbe d'action, sans picto (clé i18n par situation)
const BUTTON_LABEL_KEYS = {
  start: 'overlay.button.start',
  win: 'overlay.button.win',
  survive: 'overlay.button.survive',
  lose: 'overlay.button.lose',
};

// Dégradé du bouton selon la situation (même esprit que le bouton « C'est parti ! » du profil)
const BUTTON_BASE = 'w-full px-6 py-4 rounded-xl text-white font-black text-xl md:text-2xl uppercase tracking-widest bg-gradient-to-r shadow-lg transition transform hover:scale-105';
const BUTTON_STYLES = {
  start: 'from-[var(--cta-from)] to-[var(--cta-to)] hover:from-[var(--cta-hover-from)] hover:to-[var(--cta-hover-to)] shadow-[color:var(--shutter)]/40 text-black',
  win: 'from-[var(--witch)] to-[var(--pumpkin)] hover:from-[var(--witch-hover)] hover:to-[var(--accent-hover)] shadow-[color:var(--witch)]/40',
  survive: 'from-emerald-600 to-[var(--toxic)] hover:from-emerald-500 hover:to-emerald-400 shadow-emerald-500/40',
  lose: 'from-rose-600 to-red-500 hover:from-rose-500 hover:to-red-400 shadow-red-500/40',
};

// Phrase : picto au début et à la fin (convention de comments.js), rendus plus grands et en relief
function renderMessage(el, phrase) {
  const m = /^(\S+)\s+([\s\S]*?)\s+(\S+)$/u.exec(phrase); // [\s\S] : la phrase peut tenir sur plusieurs lignes
  if (m && m[1] === m[3]) {
    el.innerHTML = `<span class="picto">${escapeHTML(m[1])}</span>`
      + `<span>${escapeHTML(m[2])}</span>`
      + `<span class="picto">${escapeHTML(m[3])}</span>`;
  } else {
    el.innerText = phrase;
  }
}

/** @param {{ t: (key: string) => string, getLanguage: () => string }} i18n */
export function createOverlay({ t, getLanguage }) {
  const overlay = $('gameOverlay');
  const message = $('overlayMessage');
  const buttons = {
    start: $('btnStartGame'),
    next: $('btnNextLevelCenter'),
    restart: $('btnRestartCenter'),
  };
  let timeout = null;
  let chargeTimer = null;
  const chargeLayer = $('chargeLayer');
  const chargeMessage = $('chargeMessage');
  const chargeButton = $('btnChargeAction');
  const fastChargeButton = $('btnFastCharge');
  const longLifeButton = $('btnLongLife');
  const fastChargeBanner = $('fastChargeBanner'); // bandeaux : le texte reste lisible, seul le bouton rond (éclair / batterie) est actif ou non
  const longLifeBanner = $('longLifeBanner');
  const fastChargeLabel = $('fastChargeLabel');
  const longLifeLabel = $('longLifeLabel');
  const hideChargeOptions = () => { hide(fastChargeBanner); hide(longLifeBanner); };
  const scoreLines = [$('overlayScore'), $('chargeScore')]; // sous le message de l'écran central et du cadre photo

  /** Ligne de score (#37), déjà traduite ; vide = masquée. Écrite aux deux endroits : seul celui qui est affiché se voit. */
  function setScore(text) {
    for (const line of scoreLines) {
      line.textContent = text;
      if (text) show(line); else hide(line);
    }
  }

  /**
   * @param {'start'|'win'|'survive'|'lose'} situation
   * @param {'start'|'next'|'restart'} action bouton à afficher
   * @param {string|null} [messageKey]  clé i18n d'un message imposé à la place de la phrase tirée au sort (ex. inscription)
   */
  function display(situation, action, messageKey = null) {
    if (timeout) clearTimeout(timeout);
    clearInterval(chargeTimer);
    hide(chargeLayer);
    Object.values(buttons).forEach(hide);

    // Phrase discrète (le bouton doit dominer) ; pictos mis en relief via .picto
    message.className = `text-base md:text-lg font-semibold mb-5 px-2 leading-snug min-h-[60px] flex items-center justify-center gap-3 text-center ${COLOR_BY_SITUATION[situation] ?? 'text-slate-200'}`;
    renderMessage(message, messageKey ? t(messageKey) : pickComment(getLanguage(), situation));
    show(overlay);

    const btn = buttons[action];
    btn.innerText = t(BUTTON_LABEL_KEYS[situation]);
    btn.className = `${BUTTON_BASE} ${BUTTON_STYLES[situation]}`;

    const delay = situation === 'start' ? 0 : OVERLAY_BUTTON_DELAY_MS;
    timeout = setTimeout(() => show(buttons[action]), delay);
  }

  /**
   * Fin du dernier niveau : dans le cadre photo seulement (le reste de l'écran — galerie, partage, musique… — reste utilisable),
   * le message de félicitations et le bouton « Rejouer », actif tout de suite : on rejoue quand on veut, sans attente. La
   * fenêtre qui annonce la batterie gagnée est à part (ui/finaleDialog.js).
   */
  function showFinale() {
    if (timeout) clearTimeout(timeout);
    clearInterval(chargeTimer);
    hide(overlay); // pas d'écran plein : tout se passe dans le cadre photo
    hideChargeOptions();
    show(chargeLayer);
    renderMessage(chargeMessage, t('overlay.finale'));
    chargeButton.textContent = t('overlay.button.again');
    chargeButton.className = `${BUTTON_BASE} ${BUTTON_STYLES.win} charge-action`;
    chargeButton.dataset.action = 'replay';
    chargeButton.disabled = false;
    show(chargeButton);
  }

  /**
   * Batterie vide : l'appareil se recharge. Affiche « Batterie vide / En charge… / Patiente X min » (mis à jour chaque
   * seconde) UNIQUEMENT dans le cadre photo, le reste de l'écran restant visible ; une fois la charge terminée, le
   * bouton d'action apparaît dans le cadre.
   * @param {() => number} getRemainingMs  millisecondes de charge restantes
   * @param {'start'|'restart'} action  ce qu'on propose une fois chargée (restart : après une batterie vide)
   * @param {{ longLifeRights: () => number, longLifeActive: () => boolean, total: () => number }|null} [rewards]
   *   récompenses du partage (core/shareRewards.js). Deux bandeaux, chacun avec son texte et un bouton rond toujours actif : « Charge rapide » (éclair) et
   *   « Batterie longue durée » (batterie ; son bandeau n'est proposé qu'avec au moins deux kamis en galerie ou une batterie déjà gagnée, et plus une
   *   fois en service). Quand ce n'est pas encore disponible, c'est l'appelant qui l'explique au clic (une fenêtre avec un bouton Partager).
   *   Les clics sont liés par l'appelant
   */
  function showCharging(getRemainingMs, action = 'restart', rewards = null) {
    if (timeout) clearTimeout(timeout);
    clearInterval(chargeTimer);
    hide(overlay); // pas d'écran plein : tout se passe dans le cadre photo
    hide(chargeButton);
    chargeButton.disabled = false;
    hideChargeOptions();
    show(chargeLayer);
    const situation = action === 'start' ? 'start' : 'lose';

    /** Les bandeaux, tant que la charge dure : les boutons ronds restent actifs, l'explication a lieu au clic quand ce n'est pas disponible. */
    const renderOptions = () => {
      if (!rewards) { hideChargeOptions(); return; }
      const rights = rewards.longLifeRights();
      const longOffered = !rewards.longLifeActive() && (rights > 0 || rewards.total() >= 2); // moins de deux kamis en galerie : rien à partager en entier

      fastChargeLabel.textContent = t('overlay.fastCharge');
      fastChargeButton.setAttribute('aria-label', t('overlay.fastCharge'));
      show(fastChargeBanner);

      longLifeLabel.textContent = t('overlay.longLife') + (rights > 1 ? ` ×${rights}` : '');
      longLifeButton.setAttribute('aria-label', t('overlay.longLife'));
      if (longOffered) show(longLifeBanner); else hide(longLifeBanner);
    };

    const render = () => {
      const remaining = getRemainingMs();
      if (remaining > 0) {
        renderMessage(chargeMessage, t('overlay.charging', { time: formatChargeTime(remaining) }));
        renderOptions();
        return;
      }
      clearInterval(chargeTimer);
      hideChargeOptions();
      renderMessage(chargeMessage, t('overlay.charged'));
      chargeButton.textContent = t(BUTTON_LABEL_KEYS[situation]);
      chargeButton.className = `${BUTTON_BASE} ${BUTTON_STYLES[situation]} charge-action`;
      chargeButton.dataset.action = action;
      show(chargeButton);
    };
    render();
    if (getRemainingMs() > 0) chargeTimer = setInterval(render, 1000);
  }

  function close() {
    if (timeout) clearTimeout(timeout);
    clearInterval(chargeTimer);
    hide(chargeLayer);
    hide(overlay);
  }

  return { display, showCharging, showFinale, close, setScore, buttons, chargeButton, fastChargeButton, longLifeButton };
}
